import { assertValue } from '@shared/helpers'
import { IncomeType, KycInfoRequest, NoIncomeType, PoliticallyExposedPerson, PoliticalPost } from '@shared/types'

import { getCountryCodeWithLeadingPlus } from '../utils/dataUtils'
import { KycContext, SummaryInfoStepContext } from './wizard'

export interface KycPoliticalInfo {
  politicallyExposedPerson: PoliticallyExposedPerson
  politicalPost?: PoliticalPost
  otherPoliticalPost?: string
}

const getKycPoliticalInfo = (context: SummaryInfoStepContext): KycPoliticalInfo => {
  const politicalInfo = context.user.kycInfo.hasPepInfo ? context.user.kycInfo : context.politicalInfo
  return {
    politicallyExposedPerson: assertValue(
      politicalInfo?.politicallyExposedPerson,
      'politicalInfo.politicallyExposedPerson',
    ),
    politicalPost:
      politicalInfo?.politicallyExposedPerson !== PoliticallyExposedPerson.Not
        ? politicalInfo?.politicalPost
        : undefined,
    otherPoliticalPost:
      politicalInfo?.politicallyExposedPerson !== PoliticallyExposedPerson.Not &&
      politicalInfo?.politicalPost === PoliticalPost.Other
        ? politicalInfo.otherPoliticalPost
        : undefined,
  }
}

export const getKycInfoRequest = (
  context: SummaryInfoStepContext,
  shouldHandleMissingContactInfo: boolean,
): KycInfoRequest => {
  return {
    birthCountry: context.basicInfo.birthCountry,
    residenceCountry: context.basicInfo.residenceCountry,
    nationalities: context.basicInfo.nationalities,
    isNationalityUsa: context.basicInfo.isNationalityUsa,
    occupation: context.basicInfo.occupation,
    netBankNotificationsTo: context.contactInfo?.netBankNotificationsTo,
    ...getKycPoliticalInfo(context),
    taxationCountries: context.taxInfo.taxationCountries,
    taxationUsa: context.taxInfo.isTaxableInUsa,
    taxLiabilityFinland: context.taxInfo.taxLiabilityToFinland,
    isSbankPrimaryBank: context.paymentsInfo.isSbankPrimaryBank,
    incomeTypes: context.paymentsInfo.incomeTypes.includes(NoIncomeType.NoIncome)
      ? [NoIncomeType.NoIncome]
      : (context.paymentsInfo.incomeTypes as IncomeType[]),
    otherIncomeType: context.paymentsInfo.incomeTypes.includes(IncomeType.Other)
      ? context.paymentsInfo.otherIncomeType
      : undefined,
    monthlyIncome: context.paymentsInfo.monthlyIncome,
    monthlyOutcome: context.paymentsInfo.monthlyOutcome,
    abroadPaymentsIn: context.paymentsInfo.abroadPaymentsIn,
    abroadPaymentsOut: context.paymentsInfo.abroadPaymentsOut,
    cashWithdrawal: assertValue(context.paymentsInfo.cashWithdrawal, 'context.paymentsInfo.cashWithdrawal'),
    cashDeposit: assertValue(context.paymentsInfo.cashDeposit, 'context.paymentsInfo.cashDeposit'),
    ...(shouldHandleMissingContactInfo && context.contactInfo
      ? {
          email: context.contactInfo.emailAddress,
          emailMarketingAllowed: context.contactInfo.isEmailMarketingAllowed,
          phoneCountryCode: getCountryCodeWithLeadingPlus(context.contactInfo.phoneCountryCode),
          phoneNumber: context.contactInfo.phoneNumber,
          phoneMarketingAllowed: context.contactInfo.isPhoneMarketingAllowed,
        }
      : undefined),
  }
}

export const doesContextHaveUserPepInfo = (context: KycContext): boolean => {
  const kycUserInfo = assertValue(context.user, 'context.user')
  return kycUserInfo.kycInfo.hasPepInfo
}
